import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie } from 'lucide-react';
import { useCookies } from '../context/CookieContext';
import { useLanguage } from '../context/LanguageContext';
import { Switch } from './ui/switch';

const cookieText = {
  de: {
    title: 'Cookie-Einstellungen',
    description: 'Wir verwenden Cookies, um Ihre Erfahrung zu verbessern und unsere Website zu optimieren.',
    settings: 'Einstellungen',
    onlyNecessary: 'Nur notwendige',
    acceptAll: 'Alle akzeptieren',
    saveSettings: 'Einstellungen speichern',
    necessary: {
      title: 'Notwendige Cookies',
      description: 'Erforderlich für den Betrieb'
    },
    analytics: {
      title: 'Analyse-Cookies',
      description: 'Google Analytics'
    },
    marketing: {
      title: 'Marketing-Cookies',
      description: 'Personalisierte Werbung'
    }
  },
  it: {
    title: 'Impostazioni Cookie',
    description: 'Utilizziamo i cookie per migliorare la vostra esperienza e ottimizzare il nostro sito web.',
    settings: 'Impostazioni',
    onlyNecessary: 'Solo necessari',
    acceptAll: 'Accetta tutti',
    saveSettings: 'Salva impostazioni',
    necessary: {
      title: 'Cookie necessari',
      description: "Richiesti per il funzionamento"
    },
    analytics: {
      title: 'Cookie di analisi',
      description: 'Google Analytics'
    },
    marketing: {
      title: 'Cookie di marketing',
      description: 'Pubblicità personalizzata'
    }
  },
  en: {
    title: 'Cookie Settings',
    description: 'We use cookies to improve your experience and optimize our website.',
    settings: 'Settings',
    onlyNecessary: 'Only necessary',
    acceptAll: 'Accept all',
    saveSettings: 'Save settings',
    necessary: {
      title: 'Necessary Cookies',
      description: 'Required for operation'
    },
    analytics: {
      title: 'Analytics Cookies',
      description: 'Google Analytics'
    },
    marketing: {
      title: 'Marketing Cookies',
      description: 'Personalized advertising'
    }
  },
  es: {
    title: 'Configuración de Cookies',
    description: 'Utilizamos cookies para mejorar su experiencia y optimizar nuestro sitio web.',
    settings: 'Configuración',
    onlyNecessary: 'Solo necesarias',
    acceptAll: 'Aceptar todas',
    saveSettings: 'Guardar configuración',
    necessary: {
      title: 'Cookies necesarias',
      description: 'Requeridas para el funcionamiento'
    },
    analytics: {
      title: 'Cookies de análisis',
      description: 'Google Analytics'
    },
    marketing: {
      title: 'Cookies de marketing',
      description: 'Publicidad personalizada'
    }
  }
};

export default function CookieBanner() {
  const { showBanner, acceptAll, acceptNecessary, updateConsent, consent } = useCookies();
  const { language } = useLanguage();
  const text = cookieText[language] || cookieText.de;
  
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    analytics: consent?.analytics || false,
    marketing: consent?.marketing || false
  });

  const handleSaveSettings = () => {
    updateConsent({
      necessary: true,
      analytics: settings.analytics,
      marketing: settings.marketing
    });
    setShowSettings(false);
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="cookie-banner"
        >
          <div className="bg-white border-t border-[#0a0a0a] shadow-lg">
            <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6">
              {!showSettings ? (
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-10 h-10 bg-[#0a0a0a] rounded-full flex items-center justify-center hidden sm:flex">
                      <Cookie className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-[#0a0a0a] font-bold mb-1 font-['Manrope']">
                        {text.title}
                      </h3>
                      <p className="text-[#0a0a0a] text-sm max-w-xl">
                        {text.description}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-3 w-full md:w-auto">
                    <button
                      onClick={() => setShowSettings(true)}
                      className="flex-1 md:flex-none btn-pill-outline text-sm"
                      data-testid="cookie-settings-btn"
                    >
                      {text.settings}
                    </button>
                    <button
                      onClick={acceptNecessary}
                      className="flex-1 md:flex-none btn-pill-outline text-sm"
                      data-testid="cookie-reject-btn"
                    >
                      {text.onlyNecessary}
                    </button>
                    <button
                      onClick={acceptAll}
                      className="flex-1 md:flex-none btn-pill text-sm"
                      data-testid="cookie-accept-btn"
                    >
                      {text.acceptAll}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-[#0a0a0a] font-bold font-['Manrope']">
                      {text.settings}
                    </h3>
                    <button
                      onClick={() => setShowSettings(false)}
                      className="text-[#0a0a0a]"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-[#0a0a0a]">
                      <div>
                        <h4 className="text-[#0a0a0a] text-sm font-bold">{text.necessary.title}</h4>
                        <p className="text-[#0a0a0a] text-xs mt-1">{text.necessary.description}</p>
                      </div>
                      <Switch checked={true} disabled className="opacity-50" />
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-[#e5e5e5]">
                      <div>
                        <h4 className="text-[#0a0a0a] text-sm font-bold">{text.analytics.title}</h4>
                        <p className="text-[#0a0a0a] text-xs mt-1">{text.analytics.description}</p>
                      </div>
                      <Switch 
                        checked={settings.analytics}
                        onCheckedChange={(checked) => setSettings(s => ({ ...s, analytics: checked }))}
                      />
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-[#e5e5e5]">
                      <div>
                        <h4 className="text-[#0a0a0a] text-sm font-bold">{text.marketing.title}</h4>
                        <p className="text-[#0a0a0a] text-xs mt-1">{text.marketing.description}</p>
                      </div>
                      <Switch 
                        checked={settings.marketing}
                        onCheckedChange={(checked) => setSettings(s => ({ ...s, marketing: checked }))}
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleSaveSettings}
                    className="btn-pill w-full text-sm"
                  >
                    {text.saveSettings}
                  </button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
